// src/components/layout/command-palette.tsx
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Hash, LayoutGrid, Search, User } from 'lucide-react';
import { listUsers } from '@/api/endpoints/users';
import { useCanOpen } from '@/auth/section-access';
import { SECTION_HELP } from '@/lib/glossary';
import { cn } from '@/lib/cn';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { NAV_GROUPS, STORE_NAV_GROUP } from './nav-items';

/**
 * «¿A dónde voy?» — the box in the top bar, which for a year was an input
 * wired to nothing with a ⌘K hint that did nothing. It answers three
 * questions an operator types into it:
 *
 *   - a section by name («disputas», «kyc», «dinero parado»): the menu,
 *     searchable, and only the entries this operator may open;
 *   - a person by name or email: `GET /admin/users?q=`, five at most, each
 *     opening the account;
 *   - an identifier pasted from a ticket or a chat (`cm…`, `demo_order_14`):
 *     the screens that take an id, one option each, so the operator does not
 *     have to know which section the id belongs to.
 *
 * Every destination passes `canOpen`, the same map the menu and the router
 * read (ko-admin#190), so the palette never offers a door the router closes.
 */

type Item =
  | { kind: 'section'; key: string; label: string; hint: string | undefined; to: string }
  | { kind: 'user'; key: string; label: string; hint: string; id: string }
  | { kind: 'id'; key: string; label: string; hint: string; to: string; id: string };

/** Lower-case, accents stripped: «Verificación» matches «verificacion». */
function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const SECTIONS: Array<{ to: string; label: string; group: string | null }> = [
  ...NAV_GROUPS,
  STORE_NAV_GROUP,
].flatMap((g) => [
  ...g.items.map((i) => ({ to: i.to, label: i.label, group: g.label })),
  ...g.items.flatMap(
    (i) => i.children?.map((c) => ({ to: c.to, label: c.label, group: i.label })) ?? [],
  ),
]);

/** The screens that open on an id, and the section each one belongs to. */
const ID_ROUTES: Array<{ label: string; section: string; path: string }> = [
  { label: 'el usuario', section: '/users', path: '/users/$id' },
  { label: 'el pedido', section: '/orders', path: '/orders/$id' },
  { label: 'la disputa', section: '/disputes', path: '/disputes/$id' },
  { label: 'el anuncio', section: '/listings', path: '/listings/$id' },
  { label: 'el intercambio', section: '/trades', path: '/trades/$id' },
  { label: 'el torneo', section: '/tournaments', path: '/tournaments/$id' },
  { label: 'el caso de KO Hub', section: '/kohub', path: '/kohub/$id' },
  { label: 'el payout', section: '/payouts', path: '/payouts/$id' },
];

/** An id as the backend mints them (cuid) or as the demo seed names them: no spaces, 12+ chars. */
const LOOKS_LIKE_ID = /^[a-z0-9_-]{12,}$/i;

const MAX_SECTIONS = 8;
const MIN_USER_QUERY = 2;

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const debounced = useDebouncedValue(query.trim(), 250);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const userQuery = canOpen('/users') && debounced.length >= MIN_USER_QUERY ? debounced : '';
  const users = useQuery({
    queryKey: ['command-palette', 'users', userQuery],
    queryFn: () => listUsers({ q: userQuery, limit: 5 }),
    enabled: userQuery !== '',
    staleTime: 60_000,
  });

  const items = useMemo<Item[]>(() => {
    const q = fold(query.trim());
    const sections: Item[] = SECTIONS.filter((s) => canOpen(s.to))
      .filter((s) => {
        if (q === '') return true;
        const help = SECTION_HELP[s.to];
        return fold(s.label).includes(q) || (help !== undefined && fold(help).includes(q));
      })
      .slice(0, MAX_SECTIONS)
      .map((s) => ({
        kind: 'section',
        key: `section:${s.to}`,
        label: s.label,
        hint: s.group ?? undefined,
        to: s.to,
      }));

    const trimmed = query.trim();
    const ids: Item[] = LOOKS_LIKE_ID.test(trimmed)
      ? ID_ROUTES.filter((r) => canOpen(r.section)).map((r) => ({
          kind: 'id',
          key: `id:${r.path}`,
          label: `Abrir ${r.label} ${trimmed}`,
          hint: 'Si el identificador es de otra cosa, la pantalla lo dirá',
          to: r.path,
          id: trimmed,
        }))
      : [];

    const found: Item[] = (userQuery !== '' && users.data?.items ? users.data.items : []).map(
      (u) => ({
        kind: 'user',
        key: `user:${u.id}`,
        label: u.displayName || u.email,
        hint: u.displayName ? u.email : 'Cuenta',
        id: u.id,
      }),
    );

    return [...sections, ...found, ...ids];
  }, [query, canOpen, userQuery, users.data]);

  useEffect(() => {
    setActive((i) => Math.min(i, Math.max(0, items.length - 1)));
  }, [items.length]);

  const go = (item: Item) => {
    onOpenChange(false);
    if (item.kind === 'section') void navigate({ to: item.to });
    else if (item.kind === 'user') void navigate({ to: '/users/$id', params: { id: item.id } });
    else void navigate({ to: item.to, params: { id: item.id } });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[active];
      if (item) go(item);
    }
  };

  const searchingUsers = userQuery !== '' && users.isFetching;
  const optionId = (i: number) => `${listId}-opt-${i}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[12vh] z-50 w-full max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
        >
          <Dialog.Title className="sr-only">Ir a</Dialog.Title>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search size={16} className="shrink-0 text-text-muted" aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded={items.length > 0}
              aria-controls={listId}
              aria-activedescendant={items[active] ? optionId(active) : undefined}
              aria-autocomplete="list"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Una sección, una persona o un identificador…"
              aria-label="Ir a"
              className="h-12 w-full bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
            />
            <kbd className="rounded border border-border bg-graphite px-1.5 py-0.5 text-2xs text-text-muted">
              Esc
            </kbd>
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-label="Destinos"
            className="max-h-[50vh] overflow-y-auto p-1.5"
          >
            {items.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-text-muted">
                {searchingUsers
                  ? 'Buscando…'
                  : query.trim() === ''
                    ? 'Ninguna sección del menú es tuya.'
                    : 'Nada con ese nombre. Prueba con una sección, un correo o un identificador.'}
              </li>
            )}
            {items.map((item, i) => {
              const Icon =
                item.kind === 'section' ? LayoutGrid : item.kind === 'user' ? User : Hash;
              return (
                <li
                  key={item.key}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(item)}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm',
                    i === active ? 'bg-graphite text-text-primary' : 'text-text-secondary',
                  )}
                >
                  <Icon size={16} className="shrink-0 text-text-muted" aria-hidden />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate">{item.label}</span>
                    {item.hint && (
                      <span className="block truncate text-xs text-text-muted">{item.hint}</span>
                    )}
                  </span>
                  {i === active && <ArrowRight size={14} className="shrink-0 text-text-muted" />}
                </li>
              );
            })}
            {searchingUsers && items.length > 0 && (
              <li className="px-3 py-2 text-xs text-text-muted">Buscando personas…</li>
            )}
          </ul>
          <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-2xs text-text-muted">
            <span>↑↓ para moverte</span>
            <span>↵ para abrir</span>
            <span className="ml-auto">⌘K desde cualquier pantalla</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
